import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Flex, Container, Box } from 'theme-ui'
import { Nav, Heading, Loading, Button } from 'components'
import getLocalized from 'i18n'
import ReactHlsPlayer, { HlsPlayerProps } from 'react-hls-player'
import { useGetSignedUrl } from 'videoReview/hooks'
import { ErrorMessage } from 'components/Form'
import { useGetSignedSubtitles } from 'videoReview/hooks/useGetSignedSubtitles'
import { CheckboxInput } from 'components/UserInputs'

const localized = getLocalized()

export function SingleVideo() {
  const { videoId } = useParams() as { videoId: string }
  useEffect(() => {
    document.title = localized.documentTitles.adminDashboard
  }, [])
  const [get, url, loading, error] = useGetSignedUrl()
  const [getSubtitles, subtitles] = useGetSignedSubtitles()
  const [secondsTillExpiration, setSecondsTillExpiration] = useState<
    number | null
  >(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (videoId) {
      get(videoId)
      getSubtitles(videoId)
    }
  }, [videoId, get, getSubtitles])

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const firstSubtitle = subtitles?.[0]
      if (firstSubtitle) {
        const secondsLeft = getSecondsTillExpiration(firstSubtitle)
        setSecondsTillExpiration(secondsLeft)
      }
    }, 10000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [subtitles, videoId])
  return (
    <div data-cy="dashboard-screen">
      <Nav />
      <Flex
        sx={{
          width: '100%',
          minHeight: 'calc(100vh - 90px)',
          flexDirection: 'column',
          alignItems: 'center',
          bg: 'whitesmoke',
        }}
      >
        <Container>
          <Flex sx={{ pt: 60, pb: 30, justifyContent: 'space-between' }}>
            <Heading>{'Video ID: ' + videoId}</Heading>
          </Flex>
          {error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : loading || !url ? (
            <Loading />
          ) : (
            <Box>
              <Box sx={{ py: 2 }}>
                <Box sx={{ py: 2 }}>Translation sheets:</Box>

                {subtitles
                  ? subtitles.map((subtitle) =>
                      subtitle.spreadsheetLink ? (
                        <Box key={subtitle.label + subtitle.lang}>
                          {subtitle.label}({subtitle.lang}):{' '}
                          <a
                            href={subtitle.spreadsheetLink}
                            target="translation_window"
                          >
                            {subtitle.spreadsheetLink}
                          </a>
                        </Box>
                      ) : (
                        <Box key={subtitle.label + subtitle.lang}>
                          {subtitle.label}({subtitle.lang}): No sheet checked
                          out for this language
                        </Box>
                      )
                    )
                  : null}

                {secondsTillExpiration !== null &&
                secondsTillExpiration < 30 ? (
                  <Box>
                    {secondsTillExpiration < 1
                      ? 'Your subtitle access has expired '
                      : 'Your subtitle access is about to expired '}
                    <Button onClick={() => getSubtitles(videoId)}>
                      Refresh
                    </Button>
                  </Box>
                ) : null}
              </Box>
              <VideoPlayer
                src={url}
                controls={true}
                width="100%"
                height="auto"
                crossOrigin="anonymous"
                withSubtitleSelect
              >
                {subtitles
                  ? subtitles.map((subtitle) => (
                      <track
                        key={subtitle.lang}
                        src={subtitle.url}
                        kind="subtitles"
                        srcLang={subtitle.lang}
                        label={subtitle.label}
                      />
                    ))
                  : null}
              </VideoPlayer>
            </Box>
          )}
        </Container>
      </Flex>
    </div>
  )
}

const VideoPlayer = ({
  withSubtitleSelect,
  ...props
}: Omit<HlsPlayerProps, 'playerRef'> & { withSubtitleSelect?: boolean }) => {
  const playerRef = useRef<HTMLVideoElement>(null)

  const [subs, setSubs] = useState<TextTrack[]>([])
  const updateSubs = () => {
    const tracks = playerRef?.current?.textTracks
    if (tracks) {
      tracks.onaddtrack = updateSubs
      const tempSubs = []
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i]) {
          tempSubs.push(tracks[i])
        }
      }
      setSubs(tempSubs)
    }
  }
  useEffect(() => {
    const player = playerRef?.current
    if (player) {
      player.addEventListener('blur', updateSubs)
      player.addEventListener('canplay', updateSubs)
      return () => {
        player.removeEventListener('blur', updateSubs)
        player.removeEventListener('canplay', updateSubs)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerRef])
  return (
    <Box>
      {withSubtitleSelect && subs.length ? (
        <Box sx={{ py: 2 }}>
          Subtitles to display:
          {subs.map((track) => (
            <Box key={track.label}>
              <CheckboxInput
                label={track.label || track.language}
                value={track.mode === 'showing'}
                name={track.language}
                setFieldValue={(name: any, show: boolean) => {
                  track.mode = show ? 'showing' : 'disabled'
                  updateSubs()
                }}
                tabIndex={1}
              />
            </Box>
          ))}
        </Box>
      ) : null}
      <ReactHlsPlayer {...props} playerRef={playerRef} />
    </Box>
  )
}

const getSecondsTillExpiration = (subtitle: { url: string }) => {
  return (
    (new Date(
      Number(new URL(subtitle.url).searchParams.get('Expires')) * 1000
    ).getTime() -
      new Date().getTime()) /
    1000
  )
}
